<template>
  <div class="form-group" :class="{
    'validation-warning': !hasValidation,
    'validation-success': hasValidation,
    'opacity50': isDisabled
  }">
    <Button
      :label="label"
      icon="pi pi-sparkles"
      severity="warn"
      @click="openModal"
      :disabled="isDisabled"
      :data-testid="`${elemId}-open-completion`"
    />

    <!-- Modal -->
    <modal
      :show="isModalOpen"
      :title="label || 'Complete Object'"
      modal-size="modal-sm"
      :save-label="sendLabel"
      :close-label="cancelLabel"
      :save-fn="sendToAPI"
      :save-disabled-fn="() => !richTextContent || isLoading"
      @close="closeModal"
      @dismiss="closeModal"
    >
      
      <div class="object-completion-content">
        <div class="content-row">
          <div class="debug-panel" v-if="debugMode">
            <h5 class="panel-title">
              <i class="pi pi-code"></i> Object to Complete:
            </h5>
            <pre class="code-preview">{{ jsonSchemaToFillDisplay }}</pre>
          </div>
          <div class="editor-panel" :class="{ 'full-width': !debugMode }">
            <h5 class="panel-title center">{{ description }}:</h5>
            <input-rich-text
              :elem-id="richTextId"
              :placeholder="placeholder"
              label=""
              min-height="300px"
              :data-testid="`${elemId}-rich-text`"
              v-model="richTextContent">
            </input-rich-text>
          </div>
        </div>

        <div class="response-panel" v-if="apiResponse">
          <h5 class="panel-title">
            <i class="pi pi-reply"></i> API Response:
          </h5>
          <Message
            :severity="apiResponse.success ? 'success' : 'error'"
            :closable="false">
            <div v-if="apiResponse.success">
              <i class="pi pi-check"></i> Success!
              <pre class="response-data">{{ JSON.stringify(apiResponse.dados, null, 2) }}</pre>
            </div>
            <div v-if="!apiResponse.success">
              <i class="pi pi-exclamation-triangle"></i> Error: {{ apiResponse.error }}
            </div>
          </Message>
        </div>

        <div v-if="isLoading" class="loading-overlay">
          <i class="pi pi-spin pi-spinner" style="font-size: 3rem;"></i>
          <p>{{ sendLabel }}...</p>
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Modal from '../ui/Modal.vue';
import { httpService, type ApiResponse } from '../services/httpService';

interface CompletionResult {
  jsonSchemaToFill: any;
  richTextContent: string;
  apiResponse: any;
  completedAt: string;
}

const props = defineProps({
  elemId: String,
  label: String,
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  getJsonSchemaToFill: Function as any,
  extraInformationsObject: Function as any,
  successCallback: Function as any,
  errorCallback: Function as any,
  placeholder: {
    type: String,
    default: 'Enter additional content here...'
  },
  debug: {
    type: [String, Boolean],
    default: false
  },
  description: String,
  cancelLabel: {
    type: String,
    default: 'Cancelar'
  },
  sendLabel: {
    type: String,
    default: 'Enviar'
  },
  value: Object as import('vue').PropType<CompletionResult | null>
});

const emit = defineEmits<{
  'input': [value: CompletionResult];
}>();

// Reactive data
const jsonSchemaToFill = ref<any>(null);
const jsonSchemaToFillDisplay = ref<string>('');
const richTextContent = ref<string>('');
const apiResponse = ref<ApiResponse | null>(null);
const isLoading = ref<boolean>(false);
const completionResult = ref<CompletionResult | null>(null);
const isModalOpen = ref<boolean>(false);
const richTextId = ref<string>(`objectCompletionRichText_${Math.random().toString(36).substr(2, 9)}`);

// Computed properties
const isRequired = computed(() => 
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed(() => 
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed(() => 
  props.validation()
);

const debugMode = computed(() => 
  props.debug === true || props.debug === 'true'
);

// Methods
const validateRichText = () => {
  return richTextContent.value && richTextContent.value.trim().length > 0;
};

const openModal = () => {
  if (isDisabled.value) return;

  try {
    jsonSchemaToFill.value = props.getJsonSchemaToFill ? props.getJsonSchemaToFill() : null;
    if (jsonSchemaToFill.value) {
      jsonSchemaToFillDisplay.value = JSON.stringify(jsonSchemaToFill.value, null, 2);
    } else {
      jsonSchemaToFillDisplay.value = '{\n  "error": "No object provided"\n}';
    }
  } catch (error) {
    console.error('Error getting object to fill:', error);
    jsonSchemaToFillDisplay.value = '{\n  "error": "Failed to get object"\n}';
  }

  apiResponse.value = null;
  richTextContent.value = '';
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  apiResponse.value = null;
  richTextContent.value = '';
};

const sendToAPI = async () => {

  if (!richTextContent.value || !richTextContent.value.trim()) {
    apiResponse.value = {
      success: false,
      error: 'Rich text content is required'
    };
    return;
  }

  isLoading.value = true;
  apiResponse.value = null;

  const extraInformations = props.extraInformationsObject ? props.extraInformationsObject() : null;

  const payload = {
    jsonSchemaToFill: jsonSchemaToFill.value,
    richTextContent: richTextContent.value,
    extraInformationsObject: extraInformations,
    timestamp: new Date().toISOString()
  };

  try {
    const response = await httpService.post<ApiResponse>("/index.php/completions/completions/infer", payload);
    apiResponse.value = {
      success: true,
      dados: response.dados ?? response
    };

    completionResult.value = {
      jsonSchemaToFill: jsonSchemaToFill.value,
      richTextContent: richTextContent.value,
      apiResponse: response.dados ?? response,
      completedAt: new Date().toISOString()
    };

    if (props.successCallback) {
      props.successCallback({ result: (response.dados as any)?.Dados ?? (response.dados ?? response) });
    }

    // Close the modal
    closeModal();
    
    emit('input', completionResult.value);
    isLoading.value = false;
  } catch (error: any) {
    apiResponse.value = {
      success: false,
      error: error?.message || 'Network error'
    };
    isLoading.value = false;

    if (props.errorCallback) {
      props.errorCallback({ error: apiResponse.value.error ?? 'Network error' });
    }
  }
};

// Watchers
watch(() => props.value, (newVal) => {
  completionResult.value = newVal as any;
}, { immediate: true });
</script>

<style scoped>
/* Form validation states */
.form-group {
  margin-bottom: 15px;
}

.validation-warning {
  border-left: 3px solid #f39c12;
  padding-left: 10px;
}

.validation-success {
  border-left: 3px solid #00a65a;
  padding-left: 10px;
}

.opacity50 {
  opacity: 0.5;
  pointer-events: none;
}

/* Content layout */
.object-completion-content {
  position: relative;
  min-height: 400px;
}

.content-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.debug-panel {
  flex: 1 1 50%;
  min-width: 0;
}

.editor-panel {
  flex: 1 1 50%;
  min-width: 0;
}

.editor-panel.full-width {
  flex: 1 1 100%;
}

/* Panel styling */
.panel-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 15px 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-title.center {
  justify-content: center;
}

/* Code preview */
.code-preview {
  max-height: 300px;
  overflow-y: auto;
  font-size: 12px;
  background: #f5f5f5;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  padding: 15px;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Response panel */
.response-panel {
  margin-top: 20px;
}

.response-data {
  margin-top: 10px;
  font-size: 12px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
}

.loading-overlay i {
  color: #f39c12;
  margin-bottom: 20px;
}

.loading-overlay p {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

/* Responsive */
@media (max-width: 992px) {
  .content-row {
    flex-direction: column;
  }

  .debug-panel,
  .editor-panel {
    flex: 1 1 100%;
  }
}

@media (max-width: 768px) {
  .panel-title {
    font-size: 0.875rem;
  }

  .code-preview {
    font-size: 11px;
    max-height: 200px;
  }
}
</style>
